import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AuthLayout from '../components/layout/authlayout';
import Input from '../components/common/input';
import Button from '../components/common/button';
import { getPasswordStrength } from '../utils/validators';
import { resetPassword as resetPasswordAction } from '../store/actions/authActions';
import { resetPasswordSchema, validateWithZod } from '../validators/authValidator';

const ResetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [email, setEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (!emailParam) {
      navigate('/forgot-password', { replace: true });
      return;
    }
    setEmail(emailParam);
  }, [searchParams, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update password strength
    if (name === 'newPassword' && value) {
      setPasswordStrength(getPasswordStrength(value));
    } else if (name === 'newPassword' && !value) {
      setPasswordStrength(null);
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    // Keep password strength helper, but move field validation to Zod
    const validation = validateWithZod(resetPasswordSchema, {
      email,
      password: formData.newPassword,
      confirmPassword: formData.confirmPassword,
    });

    if (!validation.success) {
      const fieldErrors = validation.errors;
      const mappedErrors = {};

      if (fieldErrors.password) {
        mappedErrors.newPassword = fieldErrors.password;
      }
      if (fieldErrors.confirmPassword) {
        mappedErrors.confirmPassword = fieldErrors.confirmPassword;
      }

      setErrors(mappedErrors);
      return null;
    }

    setErrors({});
    return validation.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validatedData = validateForm();
    if (!validatedData) {
      return;
    }

    try {
      await dispatch(resetPasswordAction({
        email: validatedData.email,
        newPassword: validatedData.password,
        confirmPassword: validatedData.confirmPassword,
      }, navigate));

      // Clear form on success
      setFormData({
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordStrength(null);
    } catch (error) {
      // Error is already handled in the action
      console.error('Reset password error:', error);
    }
  };

  return (
    <AuthLayout
      title="Create New Password"
      subtitle="Your new password must be different from previously used passwords"
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* New Password Input */}
        <div>
          <Input
            label="New password"
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            placeholder="••••••••••••"
            error={errors.newPassword}
            required
          />

          {/* Password Strength Indicator */}
          {passwordStrength && (
            <div className="mt-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-600">Password Strength:</span>
                <span className={`text-xs font-bold ${passwordStrength.color === 'red' ? 'text-red-600' :
                  passwordStrength.color === 'yellow' ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                  {passwordStrength.label}
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${passwordStrength.color === 'red' ? 'bg-red-500' :
                    passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`}
                  style={{ width: `${passwordStrength.percentage}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password Input */}
        <Input
          label="Confirm password"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="••••••••••••"
          error={errors.confirmPassword}
          required
        />

        {/* Password Requirements */}
        <div className="bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg">
          <p className="text-xs text-blue-800 font-semibold mb-1">Password Requirements:</p>
          <ul className="text-xs text-blue-700 space-y-1">
            <li className="flex items-start">
              <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>At least 8 characters long</span>
            </li>
            <li className="flex items-start">
              <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Contains uppercase and lowercase letters</span>
            </li>
            <li className="flex items-start">
              <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Contains at least one number</span>
            </li>
            <li className="flex items-start">
              <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Contains at least one special character</span>
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          fullWidth
          loading={loading}
          disabled={loading}
        >
          Reset Password
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;